import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useAuth } from "@/lib/AuthContext";
import { useTheme } from "@/lib/ThemeContext";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import logger from "@/lib/logger";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";

/**
 * 12-step tutorial split in two phases:
 *   • CORE  (steps 1–6) — auto-launches on first login.
 *   • DEEP  (steps 7–12) — opt-in via the transition card after step 6.
 *
 * Step targets are CSS selectors resolved at step-enter time. If the
 * target isn't on the page we render the tooltip centered (no spotlight).
 * `route` is a path to navigate to BEFORE the step is shown — only when
 * the user is currently elsewhere — so we can anchor selectors that only
 * exist on the Dashboard without forcing a full reload.
 */
const CORE_STEPS = [
  {
    id: "welcome",
    target: null,
    title: "Welcome to MMNTM",
    body:
      "Let's take a 60-second tour of the things you'll use most. " +
      "You can skip any time, and replay this from Settings → Help.",
  },
  {
    id: "nav",
    target: '[data-testid="nav-dashboard"]',
    route: "/",
    title: "Top navigation",
    body:
      "Switch between Dashboard, Work, Analytics, and Recap from here. " +
      "Your active tab is highlighted in your accent color.",
  },
  {
    id: "pinned",
    target: '[data-testid="dashboard-todo-widget"]',
    route: "/",
    title: "Today's Pinned",
    body:
      "These are the small handful of things you've decided are worth " +
      "your attention today. Keep it short — 3 to 5 items is a great target.",
  },
  {
    id: "cmdk",
    target: null, // Bundle I — was '[data-testid="beta-badge"]' which is
    // (a) hidden on viewports < lg breakpoint causing blank spotlight on
    // small screens, and (b) misleading: the BETA badge isn't related to
    // ⌘K. Tutorial body talks about a keyboard shortcut, not a UI element,
    // so center the tooltip instead of anchoring to an arbitrary node.
    title: "Quick Capture (⌘K)",
    body:
      "Press ⌘K (or Ctrl+K) from anywhere to search, jump, or create. " +
      "It's the fastest way to get a thought out of your head.",
  },
  {
    id: "timer",
    target: '[data-testid="command-timer-display"]',
    route: "/",
    title: "Focus Timer",
    body:
      "Start a focus session right from the top bar. Pick a duration " +
      "that feels achievable — short bursts beat optimistic marathons.",
  },
  {
    id: "settings",
    target: null,
    title: "Make it yours",
    body:
      "Pop into Settings to choose a theme, tune notifications, and " +
      "set defaults that fit your brain. You're all set — happy momentum!",
  },
];

const DEEP_DIVE_STEPS = [
  {
    id: "command-palette",
    target: null, // Bundle I — same fix as core "cmdk" step. ⌘K isn't a
    // visual element, so center the tooltip rather than misanchor on the
    // BETA badge (hidden on small screens, semantically unrelated).
    title: "Command Palette",
    body:
      "Press ⌘K (Ctrl+K on Windows) to open the Command Palette. Search " +
      "tasks, projects, settings, and actions — all from your keyboard. " +
      "Your recent commands appear at the top automatically.",
  },
  {
    id: "milestones",
    target: '[data-testid="go-milestones"]',
    route: "/",
    title: "Milestones",
    body:
      "Earn XP as you use MMNTM — completing tasks, finishing focus " +
      "sessions, building streaks. Hit milestones to level up. A small " +
      "reward for staying consistent.",
  },
  {
    id: "daily-todo",
    target: '[data-testid="dashboard-todo-widget"]',
    route: "/",
    title: "Daily To-Do & Streaks",
    body:
      "Your recurring to-do list resets each day. Check off items and " +
      "build a streak — the longer your streak, the more XP you earn. " +
      "A simple way to build daily habits.",
  },
  {
    id: "feedback",
    target: '[data-testid="feedback-widget-btn"]',
    title: "Share Feedback",
    body:
      "Found a bug? Have an idea? The Feedback board lets you send " +
      "suggestions directly to the team. We read every one — your input " +
      "shapes what gets built next.",
  },
  {
    id: "settings-deep",
    target: null,
    title: "Settings",
    body:
      "Customise notifications, sounds, auto-save, keyboard shortcuts, " +
      "and more. Your theme picker, profile, and API tokens are here too. " +
      "Want to replay this tutorial? Head to Settings → Help.",
  },
  {
    id: "ready",
    target: null,
    title: "You're ready to go!",
    body:
      "That's everything. MMNTM is built to get out of your way and help " +
      "you focus on what matters. If you ever need a refresher, the " +
      "tutorial is always available in Settings.",
    extraTip: "Pro tip: Press ⌘K anytime to find anything fast.",
    finalLabel: "Start using MMNTM",
  },
];

const TOTAL_STEPS = CORE_STEPS.length + DEEP_DIVE_STEPS.length; // 12

const PADDING = 8;
const TOOLTIP_GAP = 14;

function getTargetRect(selector) {
  if (!selector) return null;
  try {
    const el = document.querySelector(selector);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return r;
  } catch {
    return null;
  }
}

function placeTooltip(rect, tipSize) {
  if (!rect) {
    return {
      top: window.innerHeight / 2 - tipSize.h / 2,
      left: window.innerWidth / 2 - tipSize.w / 2,
      placement: "center",
    };
  }
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const placement = spaceBelow >= tipSize.h + TOOLTIP_GAP + 16 ? "below"
    : spaceAbove >= tipSize.h + TOOLTIP_GAP + 16 ? "above"
    : "below";
  const top = placement === "below"
    ? rect.bottom + TOOLTIP_GAP
    : rect.top - tipSize.h - TOOLTIP_GAP;
  const rawLeft = rect.left + rect.width / 2 - tipSize.w / 2;
  const left = Math.max(16, Math.min(window.innerWidth - tipSize.w - 16, rawLeft));
  return { top, left, placement };
}

export default function Tutorial() {
  const { user } = useAuth();
  const { tokens: t } = useTheme();
  const navigate = useNavigate();

  // phase: "core" | "transition" | "deep_dive"
  const [phase, setPhase] = useState("core");
  const [stepIdx, setStepIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [tipPos, setTipPos] = useState({ top: 0, left: 0, placement: "center" });
  const tipRef = useRef(null);
  const didAutoStart = useRef(false);
  // Tracks unique step views across phases for analytics. Increments on
  // every step-viewed event so the completion event can report it.
  const stepsViewedRef = useRef(0);

  const isCore = phase === "core";
  const isDeep = phase === "deep_dive";
  const isTransition = phase === "transition";
  const stepsArr = isCore ? CORE_STEPS : isDeep ? DEEP_DIVE_STEPS : null;
  const step = stepsArr ? stepsArr[stepIdx] : null;

  // Total-shown step number across the whole tour (1..12 depending on
  // whether the user accepted the deep dive — display only).
  const displayStepNumber = isCore ? stepIdx + 1 : CORE_STEPS.length + stepIdx + 1;
  const displayTotal = isCore ? CORE_STEPS.length : TOTAL_STEPS;

  // ---- AUTO-START on first login -----------------------------------------
  useEffect(() => {
    if (!user || didAutoStart.current) return;
    didAutoStart.current = true;
    let cancelled = false;
    (async () => {
      try {
        const prefs = await api.getPreferences();
        if (!cancelled && prefs && prefs.has_completed_tutorial === false) {
          setTimeout(() => {
            if (!cancelled) {
              setPhase("core");
              setStepIdx(0);
              stepsViewedRef.current = 0;
              setOpen(true);
              trackEvent("tutorial_started", { trigger: "auto_first_login" });
            }
          }, 600);
        }
      } catch {
        // Fail-open
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ---- Replay trigger ----------------------------------------------------
  useEffect(() => {
    const onReplay = () => {
      setPhase("core");
      setStepIdx(0);
      stepsViewedRef.current = 0;
      setOpen(true);
      trackEvent("tutorial_replay_clicked", {});
      trackEvent("tutorial_started", { trigger: "manual_replay" });
    };
    window.addEventListener("mmntm:replay-tutorial", onReplay);
    return () => window.removeEventListener("mmntm:replay-tutorial", onReplay);
  }, []);

  // ---- Per-step setup: navigate, resolve rect, position tooltip ----------
  useEffect(() => {
    if (!open || isTransition || !step) return;

    if (step.route && window.location.pathname !== step.route) {
      navigate(step.route);
    }

    let raf = 0;
    let retried = false;
    const measure = () => {
      const r = getTargetRect(step.target);
      // Bundle I — Graceful skip: if a step has a target selector but the
      // element isn't in the DOM after a 500ms retry window (e.g. user
      // is on a viewport that hides the element behind a media query),
      // we centre the tooltip rather than spotlighting a blank area.
      if (step.target && !r && !retried) {
        retried = true;
        setTimeout(measure, 500);
        return;
      }
      setRect(r);
      const tipSize = tipRef.current
        ? { w: tipRef.current.offsetWidth || 360, h: tipRef.current.offsetHeight || 200 }
        : { w: 360, h: 200 };
      setTipPos(placeTooltip(r, tipSize));
    };

    raf = requestAnimationFrame(() => {
      measure();
      raf = requestAnimationFrame(measure);
    });

    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    stepsViewedRef.current += 1;
    trackEvent("tutorial_step_viewed", {
      step_id: step.id,
      step_index: displayStepNumber - 1,
      total_steps: displayTotal,
      phase,
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, stepIdx]);

  // ---- Position the centered transition card -----------------------------
  useEffect(() => {
    if (!open || !isTransition) return;
    setRect(null);
    const tipSize = tipRef.current
      ? { w: tipRef.current.offsetWidth || 380, h: tipRef.current.offsetHeight || 220 }
      : { w: 380, h: 220 };
    setTipPos(placeTooltip(null, tipSize));
  }, [open, isTransition]);

  const persistCompleted = useCallback(async () => {
    try { await api.updatePreferences({ has_completed_tutorial: true }); } catch (e) { logger.warn("Tutorial: updatePreferences failed", e); }
  }, []);

  const fireCmdkHint = useCallback(() => {
    // Decoupled trigger — CmdKHintPulse owns the localStorage gate.
    window.dispatchEvent(new CustomEvent("mmntm:show-cmdk-hint"));
  }, []);

  const closeAndComplete = useCallback((reachedDeepDive) => {
    setOpen(false);
    trackEvent("tutorial_completed", {
      total_steps: displayTotal,
      reached_deep_dive: reachedDeepDive,
      total_steps_viewed: stepsViewedRef.current,
    });
    persistCompleted();
    fireCmdkHint();
  }, [displayTotal, persistCompleted, fireCmdkHint]);

  const handleNext = useCallback(() => {
    if (isCore) {
      if (stepIdx < CORE_STEPS.length - 1) {
        setStepIdx((i) => i + 1);
      } else {
        // After last core step → transition card
        setPhase("transition");
      }
      return;
    }
    if (isDeep) {
      if (stepIdx < DEEP_DIVE_STEPS.length - 1) {
        setStepIdx((i) => i + 1);
      } else {
        closeAndComplete(true);
      }
    }
  }, [isCore, isDeep, stepIdx, closeAndComplete]);

  const handleBack = useCallback(() => {
    if (isCore) {
      if (stepIdx > 0) {
        trackEvent("tutorial_step_back", { from_step_index: stepIdx, phase });
        setStepIdx((i) => i - 1);
      }
      return;
    }
    if (isDeep) {
      if (stepIdx > 0) {
        trackEvent("tutorial_step_back", { from_step_index: stepIdx, phase });
        setStepIdx((i) => i - 1);
      } else {
        // From deep step 7, go back to transition card
        trackEvent("tutorial_step_back", { from_step_index: 0, phase });
        setPhase("transition");
      }
    }
  }, [isCore, isDeep, stepIdx, phase]);

  const handleSkip = useCallback(() => {
    setOpen(false);
    trackEvent("tutorial_skipped", {
      at_step_index: displayStepNumber - 1,
      at_step_id: step?.id || phase,
      phase,
      total_steps_viewed: stepsViewedRef.current,
    });
    persistCompleted();
    fireCmdkHint();
  }, [displayStepNumber, step, phase, persistCompleted, fireCmdkHint]);

  const handleAcceptDeepDive = useCallback(() => {
    trackEvent("tutorial_deep_dive_accepted", {});
    setPhase("deep_dive");
    setStepIdx(0);
  }, []);

  const handleDeclineDeepDive = useCallback(() => {
    trackEvent("tutorial_deep_dive_declined", {});
    if (window.location.pathname !== "/") navigate("/");
    closeAndComplete(false);
  }, [closeAndComplete, navigate]);

  // Keyboard handling
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); handleSkip(); return; }
      if (isTransition) {
        if (e.key === "Enter") { e.preventDefault(); handleAcceptDeepDive(); }
        return;
      }
      if (e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); handleNext(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); handleBack(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isTransition, handleSkip, handleNext, handleBack, handleAcceptDeepDive]);

  if (!open || !user) return null;

  const isLastDeep = isDeep && stepIdx === DEEP_DIVE_STEPS.length - 1;
  const isFirstStep = (isCore && stepIdx === 0);

  const spot = !isTransition && rect
    ? {
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      }
    : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[998]"
      role="dialog"
      aria-modal="true"
      aria-label="Tutorial"
      data-testid="tutorial-overlay"
    >
      {/* Backdrop with optional cutout */}
      {spot ? (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-auto"
          onClick={handleSkip}
          aria-hidden="true"
        >
          <defs>
            <mask id="mmntm-tutorial-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={spot.left} y={spot.top}
                width={spot.width} height={spot.height}
                rx="10" ry="10" fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%" height="100%"
            fill="rgba(0,0,0,0.72)"
            mask="url(#mmntm-tutorial-mask)"
          />
        </svg>
      ) : (
        <div
          className="absolute inset-0 pointer-events-auto"
          style={{ background: "rgba(0,0,0,0.72)" }}
          onClick={handleSkip}
          aria-hidden="true"
        />
      )}

      {/* Spotlight ring */}
      {spot && (
        <div
          className="absolute pointer-events-none rounded-[10px] transition-all duration-200"
          style={{
            top: spot.top, left: spot.left,
            width: spot.width, height: spot.height,
            boxShadow: `0 0 0 2px ${t.accent}, 0 0 24px 4px ${t.accent}55`,
          }}
          aria-hidden="true"
        />
      )}

      {/* === TRANSITION CARD === */}
      {isTransition && (
        <div
          ref={tipRef}
          className="absolute mmntm-popup rounded-xl shadow-2xl pointer-events-auto"
          style={{
            top: tipPos.top, left: tipPos.left,
            width: 380, maxWidth: "calc(100vw - 32px)",
            background: t.bgCard || t.bgSecondary,
            border: `1px solid ${t.borderColor}`,
            color: t.textPrimary,
          }}
          data-testid="tutorial-transition"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 pt-4 pb-1">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" style={{ color: t.accent }} />
              <span
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: t.textMuted }}
              >
                Core tour complete
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="p-1 rounded transition-colors"
              style={{ color: t.textMuted }}
              aria-label="Close tutorial"
              data-testid="tutorial-transition-close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="px-5 pb-4 pt-2 text-center">
            <h3
              className="text-xl font-bold mb-2"
              style={{ color: t.textPrimary, fontFamily: "'Outfit', sans-serif" }}
              data-testid="tutorial-transition-title"
            >
              You're all set!
            </h3>
            <p
              className="text-sm leading-relaxed"
              style={{ color: t.textSecondary }}
            >
              That's the basics — you're ready to go. Want a deeper look at
              what else MMNTM can do?
            </p>
          </div>

          <div
            className="flex flex-col gap-2 px-5 py-4"
            style={{ borderTop: `1px solid ${t.borderColor}` }}
          >
            <button
              onClick={handleAcceptDeepDive}
              className="w-full py-2.5 rounded-md text-[12px] font-bold transition-all"
              style={{ background: t.accent, color: t.bgPrimary }}
              data-testid="tutorial-deep-dive-accept"
            >
              Show me more
            </button>
            <button
              onClick={handleDeclineDeepDive}
              className="w-full py-2 rounded-md text-[11px] font-medium transition-colors"
              style={{ color: t.textSecondary, background: "transparent" }}
              data-testid="tutorial-deep-dive-decline"
            >
              I'm good, let's go
            </button>
          </div>
        </div>
      )}

      {/* === REGULAR STEP TOOLTIP === */}
      {!isTransition && step && (
        <div
          ref={tipRef}
          className="absolute mmntm-popup rounded-xl shadow-2xl pointer-events-auto"
          style={{
            top: tipPos.top, left: tipPos.left,
            width: 360, maxWidth: "calc(100vw - 32px)",
            background: t.bgCard || t.bgSecondary,
            border: `1px solid ${t.borderColor}`,
            color: t.textPrimary,
          }}
          data-testid="tutorial-tooltip"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 pt-3.5 pb-1">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" style={{ color: t.accent }} />
              <span
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: t.textMuted }}
              >
                Tutorial · Step {displayStepNumber} of {displayTotal}
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="p-1 rounded transition-colors"
              style={{ color: t.textMuted }}
              aria-label="Skip tutorial"
              data-testid="tutorial-skip-x"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="px-4 pb-3 pt-1">
            <h3
              className="text-base font-bold mb-1.5"
              style={{ color: t.textPrimary, fontFamily: "'Outfit', sans-serif" }}
              data-testid="tutorial-title"
            >
              {step.title}
            </h3>
            <p
              className="text-xs leading-relaxed"
              style={{ color: t.textSecondary }}
              data-testid="tutorial-body"
            >
              {step.body}
            </p>
            {step.extraTip && (
              <p
                className="text-[11px] leading-relaxed mt-2 italic"
                style={{ color: t.textMuted }}
                data-testid="tutorial-extra-tip"
              >
                {step.extraTip}
              </p>
            )}
          </div>

          <div
            className="mx-4 mb-3 h-1 rounded-full overflow-hidden"
            style={{ background: t.bgTertiary }}
            aria-hidden="true"
          >
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${(displayStepNumber / displayTotal) * 100}%`,
                background: t.accent,
              }}
            />
          </div>

          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderTop: `1px solid ${t.borderColor}` }}
          >
            <button
              onClick={handleSkip}
              className="text-[11px] font-medium transition-colors"
              style={{ color: t.textMuted }}
              data-testid="tutorial-skip-btn"
            >
              Skip tour
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={handleBack}
                disabled={isFirstStep}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: t.bgTertiary,
                  color: t.textPrimary,
                  border: `1px solid ${t.borderColor}`,
                }}
                data-testid="tutorial-back-btn"
              >
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-bold transition-all"
                style={{ background: t.accent, color: t.bgPrimary }}
                data-testid="tutorial-next-btn"
              >
                {isLastDeep ? (step.finalLabel || "Done") : "Next"}
                {!isLastDeep && <ChevronRight className="h-3 w-3" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
